import { apiClient } from '@/shared/services/api/client';
import { unwrapEnvelope } from '@/shared/services/api/envelope';
import type { ApiEnvelope } from '@/shared/services/api/envelope';
import type {
  MasterExportRequest,
  MasterImportResult,
  MasterPage,
  MasterQuery,
  PagedContent,
} from '@/features/masters';
import { mapTemplateListing, scopeOfQuery, scopeQuery } from '@/features/templates';
import type { TemplateDetail, TemplateRecord } from '@/features/templates';
import type {
  SubmissionDecision,
  SubmissionDecisionResult,
  TemplateFormSubmissionDetail,
  TemplateFormSubmissionResult,
  TemplateFormSubmissionRow,
  TemplateFormSubmitRequest,
  PurchaseRequestSummary,
} from '../models/templateForm.types';
import { isAwaitingMe, standardDecisionMessage } from '../models/templateForm.types';

/**
 * The calls a forms screen makes -- Purchase Requests (NB-355), under its own paths (the Default Template Forms
 * screen that shared this contract was removed on 2026-10-16): the active template cards, the submitted forms, one form, a submit and a
 * decision.
 */
export interface FormsApi {
  listActiveTemplates: (query: MasterQuery) => Promise<MasterPage<TemplateRecord>>;
  /**
   * One active template's form and stored mapping, read through the screen's own path (Purchase Requests:
   * `GET /purchase-requests/templates/{id}`), so a role without the Templates grant can use it. A screen without
   * one reads the Templates feature's detail.
   */
  getTemplate?: (id: number) => Promise<TemplateDetail>;
  listSubmissions: (query: MasterQuery) => Promise<MasterPage<TemplateFormSubmissionRow>>;
  getSubmission: (id: number) => Promise<TemplateFormSubmissionDetail>;
  submit: (payload: TemplateFormSubmitRequest) => Promise<TemplateFormSubmissionResult>;
  decide: (
    id: number,
    decision: SubmissionDecision,
    comment: string,
    stepSequence?: number | null,
  ) => Promise<SubmissionDecisionResult>;
  /** The status cards' counts for the selected hierarchy; a screen without cards has none. */
  summary?: (query: MasterQuery) => Promise<PurchaseRequestSummary>;
  /** The list as a CSV or Excel file -- the view (its query and visible columns) or every record. */
  exportList?: (request: MasterExportRequest) => Promise<Blob>;
  /**
   * Records from a CSV or Excel file, in the entity the page is for.
   *
   * `templateId` (NB-551) files every request in it under that PR Template: the file then carries no PR
   * Template column, and its rows are read and validated against that template. Without one the server reads
   * each request's template from its own row, as it always has.
   */
  importFile?: (
    file: File,
    legalEntityIds: readonly number[],
    templateId?: number | null,
  ) => Promise<MasterImportResult>;
  /**
   * The import file's layout, with an example.
   *
   * `templateId` asks for that template's own file -- its fields as columns, and no PR Template column.
   *
   * `legalEntityIds` is the entity the file will be filled in for: an Excel template carries a dropdown on
   * every column the screen fills from one, read from that entity's own masters, so the values offered are
   * the ones the import will accept.
   */
  importTemplate?: (
    format?: 'xlsx' | 'csv',
    templateId?: number | null,
    legalEntityIds?: readonly number[],
  ) => Promise<Blob>;
  /** The number the next record will be saved under, shown on the Add page and submitted with it (Purchase Requests). */
  nextNumber?: () => Promise<string>;
  /** A record edited: the submit body, saved over it under its own number (Purchase Requests). */
  update?: (id: number, payload: TemplateFormSubmitRequest) => Promise<TemplateFormSubmissionResult>;
  /** A record deleted softly: marked Inactive, still listed (Purchase Requests). */
  remove?: (id: number) => Promise<TemplateFormSubmissionResult>;
  /**
   * Whether a record could be deleted now, asked before the delete dialog opens (Purchase Requests, 2026-10-13):
   * resolves when nothing holds it, and rejects with the platform's 409 -- the BIDs holding it and their count --
   * when something does. Nothing is written.
   */
  deleteImpact?: (id: number) => Promise<void>;
}

/** The filter dialog's conditions, as `filter=property:comparison:value`. */
function filterParams(query: MasterQuery): string[] {
  return (query.filters ?? [])
    .filter((condition) => condition.column !== '' && condition.value.trim() !== '')
    .map((condition) => `${condition.column}:${condition.comparison}:${condition.value.trim()}`);
}

/** The list's own extras -- the two status filters and the hierarchy -- as the server reads them. */
function listExtras(query: MasterQuery): Record<string, string> {
  const params: Record<string, string> = {};
  const state = (query as { workflowState?: unknown }).workflowState;
  if (typeof state === 'string' && state !== '') params.workflowState = state;
  const processing = (query as { processingStatus?: unknown }).processingStatus;
  if (typeof processing === 'string' && processing !== '') params.processingStatus = processing;
  Object.assign(params, scopeQuery(scopeOfQuery(query)));
  return params;
}

interface FormsApiConfig {
  /** `GET` the active template cards. */
  templatesPath: string;
  /** Whether one template is read at `${templatesPath}/{id}` rather than through the Templates feature. */
  templateDetail?: boolean;
  /** `GET` the list, `POST` a submit; `/{id}` one form, `/{id}/decisions` a decision. */
  submissionsPath: string;
  /** The status cards' endpoint, where the screen has them (Purchase Requests). */
  summaryPath?: string;
  /** Whether the screen exports and imports its list (`/export`, `/import`, `/import-template`). */
  transfers?: boolean;
  /** `GET` the next record number for the Add page, where the screen shows its records' numbers up front. */
  nextNumberPath?: string;
  /** Whether records are edited (`PUT /{id}`) and deleted softly (`DELETE /{id}`) -- Purchase Requests. */
  edits?: boolean;
  /** What the screen calls its records, for the fallback messages: one read, many read, one submitted. */
  noun: { one: string; many: string; submit: string };
  /** The `env.ENABLE_MOCKS` answers, or null against the server. */
  mocks: FormsApi | null;
}

/* A refusal is the screen's news, not grounds to throw the user out of the shell. */
const allowForbidden = { _allowForbidden: true } as const;

function sortParam(query: MasterQuery): string | undefined {
  return query.sortField && query.sortOrder
    ? `${query.sortField},${query.sortOrder === 1 ? 'asc' : 'desc'}`
    : undefined;
}

/** One screen's client, over its own paths. */
export function createFormsApi({
  templatesPath,
  templateDetail = false,
  submissionsPath,
  summaryPath,
  transfers = false,
  nextNumberPath,
  edits = false,
  noun,
  mocks,
}: FormsApiConfig): FormsApi {
  if (mocks) return mocks;

  return {
    /*
      No `kind` and no `status`: the server fixes both. The hierarchy selection travels as the query's extras
      (`legalEntityIds=1,2`, ...); a template mapped nowhere is not listed for it.
    */
    listActiveTemplates: async (query) => {
      const params: Record<string, string | number> = { page: query.page, size: query.size };
      const search = query.search?.trim();
      if (search) params.search = search;
      const sort = sortParam(query);
      if (sort) params.sort = sort;
      Object.assign(params, scopeQuery(scopeOfQuery(query)));
      const categories = (query as { categoryIds?: unknown }).categoryIds;
      if (typeof categories === 'string' && categories !== '') params.categoryIds = categories;
      const response = await apiClient.get<ApiEnvelope<PagedContent<TemplateRecord> | TemplateRecord[]>>(
        templatesPath,
        { ...allowForbidden, params },
      );
      return mapTemplateListing(response, 'Unable to load the active templates. Please try again.');
    },

    ...(templateDetail
      ? {
          getTemplate: async (id: number) =>
            unwrapEnvelope(
              await apiClient.get<ApiEnvelope<TemplateDetail>>(`${templatesPath}/${String(id)}`, allowForbidden),
              'Unable to load the template. Please try again.',
            ),
        }
      : {}),

    listSubmissions: async (query) => {
      const params: Record<string, string | number | boolean | string[]> = { page: query.page, size: query.size };
      const search = query.search?.trim();
      if (search) params.search = search;
      const sort = sortParam(query);
      if (sort) params.sort = sort;
      /* My Approvals: the server resolves "me" from the token; no user id is ever sent. */
      if (isAwaitingMe(query)) params.awaitingMe = true;
      /* The status filters (Purchase Requests) and the hierarchy, carried as the query's extras. */
      Object.assign(params, listExtras(query));
      const filters = filterParams(query);
      if (filters.length > 0) params.filter = filters;
      const body = unwrapEnvelope(
        await apiClient.get<ApiEnvelope<PagedContent<TemplateFormSubmissionRow>>>(submissionsPath, {
          ...allowForbidden,
          params,
        }),
        `Unable to load the ${noun.many}. Please try again.`,
      );
      return { rows: body.content, total: body.totalElements ?? body.content.length };
    },

    getSubmission: async (id) =>
      unwrapEnvelope(
        await apiClient.get<ApiEnvelope<TemplateFormSubmissionDetail>>(
          `${submissionsPath}/${String(id)}`,
          allowForbidden,
        ),
        `Unable to load the ${noun.one}. Please try again.`,
      ),

    submit: async (payload) =>
      unwrapEnvelope(
        await apiClient.post<ApiEnvelope<TemplateFormSubmissionResult>>(submissionsPath, payload, allowForbidden),
        `Unable to submit the ${noun.submit}. Please try again.`,
      ),

    ...(summaryPath
      ? {
          summary: async (query: MasterQuery) =>
            unwrapEnvelope(
              await apiClient.get<ApiEnvelope<PurchaseRequestSummary>>(summaryPath, {
                ...allowForbidden,
                params: {
                  ...scopeQuery(scopeOfQuery(query)),
                  /* The Filter dialog's conditions count on the cards as on the list (2026-10-09). */
                  ...(filterParams(query).length > 0 ? { filter: filterParams(query) } : {}),
                },
              }),
              'Unable to load the status summary. Please try again.',
            ),
        }
      : {}),

    ...(nextNumberPath
      ? {
          nextNumber: async () =>
            unwrapEnvelope(
              await apiClient.get<ApiEnvelope<{ referenceNo: string }>>(nextNumberPath, allowForbidden),
              `Unable to load the ${noun.one} number. Please try again.`,
            ).referenceNo,
        }
      : {}),

    ...(edits
      ? {
          update: async (id: number, payload: TemplateFormSubmitRequest) =>
            unwrapEnvelope(
              await apiClient.put<ApiEnvelope<TemplateFormSubmissionResult>>(
                `${submissionsPath}/${String(id)}`,
                payload,
                allowForbidden,
              ),
              `Unable to update the ${noun.one}. Please try again.`,
            ),
          remove: async (id: number) =>
            unwrapEnvelope(
              await apiClient.delete<ApiEnvelope<TemplateFormSubmissionResult>>(
                `${submissionsPath}/${String(id)}`,
                allowForbidden,
              ),
              `Unable to delete the ${noun.one}. Please try again.`,
            ),
          deleteImpact: async (id: number) => {
            await apiClient.get<ApiEnvelope<unknown>>(
              `${submissionsPath}/${String(id)}/delete-impact`,
              allowForbidden,
            );
          },
        }
      : {}),

    ...(transfers
      ? {
          exportList: async (request: MasterExportRequest) => {
            const params: Record<string, string | string[]> = {
              scope: request.scope.rows === 'all' ? 'all' : 'view',
              format: request.scope.format,
              /* The hierarchy travels with both scopes: "all" is every request at it. */
              ...scopeQuery(scopeOfQuery(request.query)),
            };
            if (request.scope.rows === 'view') {
              const search = request.query.search?.trim();
              if (search) params.search = search;
              Object.assign(params, listExtras(request.query));
              const filters = filterParams(request.query);
              if (filters.length > 0) params.filter = filters;
              const sort = sortParam(request.query);
              if (sort) params.sort = sort;
              if (request.columns.length > 0) params.columns = request.columns.join(',');
            }
            const response = await apiClient.get<Blob>(`${submissionsPath}/export`, {
              ...allowForbidden,
              params,
              responseType: 'blob',
            });
            return response.data;
          },
          /* The file's own bytes, as the masters' import sends them -- see createArtboardMasterImport. */
          importFile: async (
            file: File,
            legalEntityIds: readonly number[],
            templateId?: number | null,
          ) =>
            unwrapEnvelope(
              await apiClient.post<ApiEnvelope<MasterImportResult>>(`${submissionsPath}/import`, file, {
                ...allowForbidden,
                headers: { 'Content-Type': file.type === '' ? 'application/octet-stream' : file.type },
                params: {
                  ...(legalEntityIds.length > 0 ? { legalEntityIds: legalEntityIds.join(',') } : {}),
                  ...(templateId == null ? {} : { templateId }),
                },
              }),
              `Unable to import the ${noun.many}. Please try again.`,
            ),
          importTemplate: async (
            format: 'xlsx' | 'csv' = 'xlsx',
            templateId?: number | null,
            legalEntityIds: readonly number[] = [],
          ) => {
            const response = await apiClient.get<Blob>(`${submissionsPath}/import-template`, {
              ...allowForbidden,
              params: {
                format,
                ...(templateId == null ? {} : { templateId }),
                /* The entity whose locations, categories and products the dropdowns are read from. */
                ...(legalEntityIds.length > 0 ? { legalEntityIds: legalEntityIds.join(',') } : {}),
              },
              responseType: 'blob',
            });
            return response.data;
          },
        }
      : {}),

    decide: async (id, decision, comment, stepSequence) => {
      const response = await apiClient.post<ApiEnvelope<TemplateFormSubmissionDetail>>(
        `${submissionsPath}/${String(id)}/decisions`,
        {
          decision,
          comment: comment.trim() === '' ? null : comment.trim(),
          ...(typeof stepSequence === 'number' ? { stepSequence } : {}),
        },
        allowForbidden,
      );
      const submission = unwrapEnvelope(response, 'Unable to record the decision. Please try again.');
      const message = response.data.message?.trim();
      return { submission, message: message ? message : standardDecisionMessage(submission) };
    },
  };
}
